import { map, share, flatMap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { DataService, ApiService, InspectionPluginService, LanguageService } from '../../shared/services/index';
import { InspectionPlugin } from '../../shared/models/index';
import * as _ from 'lodash';
import { ApiMode } from '../../utils/enums';
import { OfflineApiService } from '../../shared/services/offlineApi.service';
import { AppConstants } from '../../utils/appConstants.class';
import { InspectionFactoryService } from '../../shared/services/inspectionFactory.service';
import { LogService } from '../../shared/services/log.service';

@Injectable()
export class TrainingProposalsPluginService extends InspectionPluginService {
  private traineesLoader: Observable<any>;
  private traineeKeys: any = [];
  private trainees: any = [];

  private momentsLoader: Observable<any>;
  private momentKeys: any = [];
  private moments: any = [];

  private coursesLoader: Observable<any>;
  private courseKeys: any = [];
  private courses: any = [];

  constructor(
    protected apiService: ApiService,
    protected dataService: DataService,
    private language: LanguageService,
    offlineApi: OfflineApiService,
    factory: InspectionFactoryService,
    private logService: LogService
  ) {
    super(apiService, dataService, offlineApi, factory);
    this.loadCoursesData();
    this.loadMomentsData();
    this.loadTraineesData();
    this.language.onLanguageChange().subscribe((value) => this.applyTranslations());
  }

  public updateTrainingProposals(inspectionId: string, pluginId: string, trainingProposals: any): Observable<any> {
    return this._updateTrainingProposals(inspectionId, pluginId, trainingProposals).pipe(
      map((res: InspectionPlugin) => {
        return this.dataService.updatePlugin(inspectionId, res);
      })
    );
  }
  private _updateTrainingProposals(inspectionId: string, pluginId: string, trainingProposals: any): Observable<any> {
    return this.factory.updatePluginConfig(inspectionId, null, pluginId, trainingProposals, 'TrainingProposals').pipe(
      flatMap((plugin) => {
        if (this.isInspectionInOffline(inspectionId)) {
          return of(plugin);
        }
        return this.apiService.post(plugin.postServerUrl, plugin.config);
      })
    );
  }

  public getTrainers() {
    return this.getItems(this.traineesLoader, this.trainees);
  }

  public getCourses() {
    return this.getItems(this.coursesLoader, this.courses);
  }

  public getMoments() {
    return this.getItems(this.momentsLoader, this.moments);
  }

  public getItems(loader: Observable<any>, items: any): Observable<any> {
    if (loader) return loader;
    return new BehaviorSubject(items).asObservable();
  }

  public loadTraineesData() {
    const self = this;
    this.traineesLoader = this.loadTrainees().pipe(
      map((res: any) => {
        this.traineesLoader = null;
        const trainees = res;
        _.map(trainees, (el) => {
          self.traineeKeys.push({ label: el.Label, value: el.Value });
        });
        this.applyTranslations();
        return this.trainees;
      }),
      share()
    );
    this.traineesLoader.subscribe(
      (data: any) => {},
      (err: any) => this.logService.logError(err, 'Trainees loading error')
    );
  }

  public loadMomentsData() {
    const self = this;
    this.momentsLoader = this.loadMoments().pipe(
      map((res: any) => {
        this.momentsLoader = null;
        const moments = res;
        _.map(moments, (el) => {
          self.momentKeys.push({ label: el.Label, value: el.Value });
        });
        this.applyTranslations();
        return this.moments;
      }),
      share()
    );
    this.momentsLoader.subscribe(
      (data: any) => {},
      (err: any) => this.logService.logError(err, 'Moment loading error')
    );
  }

  public loadCoursesData() {
    const self = this;
    this.coursesLoader = this.loadCourses().pipe(
      map((res: any) => {
        this.coursesLoader = null;
        const courses = res;
        _.map(courses, (el) => {
          self.courseKeys.push({ label: el.Label, value: el.Value, productLineId: el.ProductLineId });
        });
        this.applyTranslations();
        return this.courses;
      }),
      share()
    );
    this.coursesLoader.subscribe(
      (data: any) => {},
      (err: any) => this.logService.logError(err, 'Courses loading error')
    );
  }

  private loadTrainees() {
    const url = '/inspections/plugins/trainingproposals/trainees/list';
    return this.offlineApi.getWithMode(url, ApiMode.Offline, AppConstants.INDEXEDDB_TABLES.trainees);
  }

  private loadCourses() {
    const url = '/inspections/plugins/trainingproposals/courses/list';
    return this.offlineApi.getWithMode(url, ApiMode.Offline, AppConstants.INDEXEDDB_TABLES.courses);
  }

  private loadMoments() {
    const url = '/inspections/plugins/trainingproposals/moments/list';
    return this.offlineApi.getWithMode(url, ApiMode.Offline, AppConstants.INDEXEDDB_TABLES.moments);
  }

  getValue(value: any, array: any): string {
    const obj = _.find(array, ['value', value]);
    return obj ? obj.label : '';
  }

  private applyTranslations() {
    this.courses = [];
    this.moments = [];
    this.trainees = [];
    this.copyTranslationValues(this.courseKeys, this.courses, this.language);
    this.copyTranslationValues(this.momentKeys, this.moments, this.language);
    this.copyTranslationValues(this.traineeKeys, this.trainees, this.language);
  }
}
